// src/ForgotPassword.jsx
// LOOP AUTH IOS REGISTER RELIABILITY V28 — password reset has native-safe HTTP fallback.
// Sends Firebase password reset email, forcing production reset URL so users land on your nice UI.

import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { firebaseAuth } from "./firebase";
import { isIosCapacitor, httpRequestJson } from "./utils/nativeHttp";

const LOOP_AUTH_FUNCTIONS_BASE = "https://us-central1-web-app-d9799.cloudfunctions.net";
const LOOP_PASSWORD_RESET_HTTP_URL = `${LOOP_AUTH_FUNCTIONS_BASE}/sendPasswordResetEmailHttpV28`;

function withTimeout(promise, ms, label = "timeout") {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([
    Promise.resolve(promise).finally(() => {
      try { if (timer) clearTimeout(timer); } catch (_) {}
    }),
    timeout,
  ]);
}

function friendlyResetError(err) {
  const raw = String(err?.code || err?.message || err || "").toLowerCase();
  if (raw.includes("invalid-email")) return "El correo no tiene un formato válido.";
  if (raw.includes("too-many") || raw.includes("429")) return "Hiciste varios intentos seguidos. Espera unos minutos e intenta nuevamente.";
  if (raw.includes("network") || raw.includes("timeout") || raw.includes("load failed")) return "No pudimos enviar el correo. Revisa tu conexión e intenta nuevamente.";
  return "No pudimos enviar el correo. Intenta nuevamente.";
}

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const emailPrefill = location?.state?.emailPrefill || "";
  const from = location?.state?.from || "/";

  const [email, setEmail] = useState(String(emailPrefill || ""));
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  // ✅ Force your production URL (avoid localhost + avoid Firebase ugly /__/auth/action UI)
  // Make sure this domain is in Firebase Auth -> Authorized domains
  const resetUrl = useMemo(() => "https://www.loopreservas.com/reset-password", []);

  const handleSend = async (e) => {
    e?.preventDefault();
    setError("");
    setMsg("");
    setLoading(true);

    try {
      const cleanEmail = String(email || "").trim().toLowerCase();
      if (!cleanEmail) {
        setError("Escribe tu correo.");
        return;
      }

      const sendViaHttp = () => withTimeout(httpRequestJson({
        url: LOOP_PASSWORD_RESET_HTTP_URL,
        method: "POST",
        data: {
          email: cleanEmail,
          continueUrl: resetUrl,
          source: isIosCapacitor() ? "ios_capacitor_forgot_password_v28" : "web_forgot_password_v28",
        },
      }), 30000, "password-reset-http-timeout");

      if (isIosCapacitor()) {
        await sendViaHttp();
      } else {
        try {
          await withTimeout(sendPasswordResetEmail(firebaseAuth, cleanEmail, {
            url: resetUrl,
            handleCodeInApp: true,
          }), 30000, "password-reset-sdk-timeout");
        } catch (sdkErr) {
          console.warn("[ForgotPassword] Firebase SDK reset failed; trying HTTP fallback", sdkErr);
          await sendViaHttp();
        }
      }

      // ✅ Security-friendly message (do not reveal if email exists)
      setMsg("Si el correo está registrado, te enviaremos un enlace para restablecer tu contraseña.");
    } catch (err) {
      setError(friendlyResetError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-md border overflow-hidden mt-8">
        <div className="p-6">
          <h2 className="text-2xl font-semibold text-slate-800">Recuperar contraseña</h2>
          <p className="text-sm text-slate-500 mt-1">Escribe tu correo y te enviaremos un enlace.</p>

          {error ? (
            <div
              className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              style={{ whiteSpace: "pre-wrap" }}
            >
              {error}
            </div>
          ) : null}

          {msg ? (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {msg}
            </div>
          ) : null}

          <form onSubmit={handleSend} className="space-y-3 mt-5">
            <div>
              <label className="block text-sm text-slate-600 mb-1">Correo</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
                className="w-full px-3 py-2 border rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                required
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg font-semibold"
            >
              {loading ? "Enviando..." : "Enviar enlace"}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true, state: { from } })}
                className="text-slate-500 underline hover:text-slate-700"
              >
                Volver a login
              </button>

              <button
                type="button"
                onClick={() => navigate(from, { replace: true })}
                className="text-slate-500 underline hover:text-slate-700"
              >
                Ir al inicio
              </button>
            </div>
          </form>

          <div className="mt-4 text-xs text-slate-400">
            Si no ves el correo, revisa Spam / No deseado.
          </div>
        </div>
      </div>
    </div>
  );
}